// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { memo, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  StoreMapCoveragePoint,
  StoreMapCoverageQuality,
  StoreMapCoverageThreshold,
} from "@/api/hooks/use-store-map-coverage";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Best signal first, matching the order the API returns its thresholds in.
const QUALITY_ORDER: StoreMapCoverageQuality[] = ["excellent", "good", "fair", "poor", "unknown"];

interface StoreMapCoverageLegendProps {
  points: StoreMapCoveragePoint[];
  thresholds: StoreMapCoverageThreshold[];
}

/**
 * Colour key plus how many cameras fall in each band.
 *
 * The dBm cutoffs are read off the API's thresholds rather than restated here,
 * so moving a band is a backend change and this legend follows it.
 */
export function StoreMapCoverageLegend({ points, thresholds }: StoreMapCoverageLegendProps) {
  const { t } = useTranslation("back-office");
  const floors = new Map(thresholds.map((threshold) => [threshold.quality, threshold.minRssi]));
  const counts = new Map<StoreMapCoverageQuality, number>();
  for (const point of points) counts.set(point.quality, (counts.get(point.quality) ?? 0) + 1);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {QUALITY_ORDER.filter((quality) => counts.has(quality)).map((quality) => {
        const floor = floors.get(quality);
        return (
          <div key={quality} className="flex items-center gap-1.5 text-sm">
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: COVERAGE_COLORS[quality] }}
              aria-hidden
            />
            <span>{t(`stores.map.coverage.quality.${quality}`)}</span>
            {typeof floor === "number" && <span className="text-muted-foreground tabular-nums">≥ {floor} dBm</span>}
            <span className="font-medium tabular-nums">{counts.get(quality)}</span>
          </div>
        );
      })}
    </div>
  );
}

interface StoreMapCoverageOverlayProps {
  points: StoreMapCoveragePoint[];
  /** Natural pixel size of the map image — the coordinate space the points are recorded in. */
  naturalWidth: number;
  naturalHeight: number;
  /** Base64 PNG, white inside the building. Absent when the API could not derive one. */
  storeMask?: string;
  className?: string;
}

/**
 * Whether the clip mask has finished decoding, or `true` when there is none.
 *
 * A data URI is not free: the browser still has to decode it, and an SVG mask
 * referencing an image that has not decoded yet paints nothing through it. Drawing
 * the cloud before that resolves gives a visible frame of the wrong picture, so the
 * caller waits for a real decode rather than trusting the bytes being in hand.
 */
function useMaskReady(storeMask: string | undefined): boolean {
  const [readyFor, setReadyFor] = useState<string | null>(null);

  useEffect(
    function decodeMaskBeforeRevealingCloud() {
      if (!storeMask) return;
      let cancelled = false;
      const image = new Image();
      image.src = `data:image/png;base64,${storeMask}`;
      image
        .decode()
        // A mask that will not decode must not hide the cloud forever — fall through
        // to drawing unclipped, which is what happens when there is no mask at all.
        .catch(() => undefined)
        .then(() => {
          if (!cancelled) setReadyFor(storeMask);
        });
      return function cancelPendingDecode() {
        cancelled = true;
      };
    },
    [storeMask],
  );

  return !storeMask || readyFor === storeMask;
}

/**
 * Signal colours, keyed by the grade the API assigns.
 *
 * Fixed hex rather than theme tokens: these sit on the map image, which is a
 * white raster in both themes, so they must stay legible against white rather
 * than follow the surrounding surface.
 */
export const COVERAGE_COLORS: Record<StoreMapCoverageQuality, string> = {
  excellent: "#059669",
  good: "#65a30d",
  fair: "#d97706",
  poor: "#dc2626",
  unknown: "#94a3b8",
};

/**
 * Radius of one sample's contribution, in map pixels.
 *
 * Cameras sit roughly 175px apart on a typical store plan, so a radius in this
 * range overlaps neighbours enough to read as a field rather than as beads on a
 * string. It is deliberately finite: floor area further than this from any
 * camera stays fully transparent, which is the confidence mask — unsampled
 * ground shows the bare map instead of a colour nobody measured.
 */
const SAMPLE_RADIUS = 320;

/**
 * Opacity ramp from a sample's centre out to its edge.
 *
 * The core is the density of the cloud where a camera actually stands; the mid
 * stop keeps the falloff gentle so neighbouring samples merge into a field
 * rather than stacking as visible discs. The edge must stay at zero — that is
 * what leaves unsampled floor showing the bare map.
 */
const CLOUD_CORE_OPACITY = 0.8;
const CLOUD_MID_OPACITY = 0.32;

/**
 * Opacity of the assembled cloud layer, applied once over the blended field.
 *
 * Multiplies with the ramp above, so the densest point lands at
 * CLOUD_CORE_OPACITY × this. Kept separate because it tunes a different thing:
 * the ramp controls how samples merge into each other, this controls how much
 * floor plan survives underneath. The dots are outside it and stay fully opaque.
 */
const CLOUD_LAYER_OPACITY = 0.8;

// Dots are drawn a touch larger than a pixel so they stay clickable when the
// whole 7000px-wide plan is squeezed into a 600px-tall box.
const DOT_RADIUS = 26;

/** Stand-in for a reading the device never reported — `avgRssi` and `uptimePct` are both optional. */
const EMPTY_VALUE = "—";

function round(value: number | null | undefined, digits: number): string {
  return typeof value === "number" ? value.toFixed(digits) : EMPTY_VALUE;
}

interface CoverageTooltipRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

function CoverageTooltipRow({ label, value, mono }: CoverageTooltipRowProps) {
  return (
    <>
      <dt className="text-background/70">{label}</dt>
      <dd className={cn("text-right tabular-nums", mono && "font-mono")}>{value}</dd>
    </>
  );
}

/**
 * One camera position, with its reading behind a tooltip.
 *
 * The dot is the Radix trigger itself (`asChild`) rather than a wrapper: Popper
 * anchors off `getBoundingClientRect`, which on an SVG element already accounts
 * for the pan/zoom transform on the box above, so the card tracks the dot at
 * every scale without the overlay having to convert coordinates.
 *
 * Memoised because a dot is not a bare `<circle>` any more — it carries a Radix
 * root, its contexts and its own open state — and a store can field hundreds.
 */
const CoveragePointDot = memo(function CoveragePointDot({ point }: { point: StoreMapCoveragePoint }) {
  const { t } = useTranslation("back-office");
  const quality = t(`stores.map.coverage.quality.${point.quality}`);
  const rssi = round(point.avgRssi, 1);
  const uptime = round(point.uptimePct, 0);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <circle
          cx={point.x}
          cy={point.y}
          r={DOT_RADIUS}
          fill={COVERAGE_COLORS[point.quality]}
          stroke="#ffffff"
          strokeWidth={8}
          // `cursor` inherits, and the pan surface above sets `grab`. A dot is not
          // draggable — it is a thing you read — so it states its own cursor rather
          // than inheriting a promise the overlay does not keep.
          className="cursor-default"
          // The tooltip is pointer-only — a dot is not focusable, and making every
          // camera a tab stop would bury the rest of the page. Screen readers get
          // the same reading off the label instead.
          aria-label={t("stores.map.coverage.pointSummary", {
            device: point.deviceName,
            rssi,
            samples: point.sampleCount,
            uptime,
            aps: point.apCount,
          })}
        />
      </TooltipTrigger>
      {/* `pointer-events-none` is load-bearing, not cosmetic. The card opens above the
          dot, so approaching from above puts the cursor under it the moment it renders
          — the pointer would leave the dot, Radix would close, the cursor would land
          back on the dot, and it would reopen, flickering. Nothing in here is
          interactive, so letting the pointer straight through breaks that loop at the
          source; the offset then keeps the card clear of the dot's white ring. */}
      <TooltipContent side="top" sideOffset={8} className="pointer-events-none px-3 py-2">
        <div className="space-y-1.5">
          <p className="font-medium">{point.deviceName}</p>
          <p className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: COVERAGE_COLORS[point.quality] }}
              aria-hidden
            />
            {quality}
          </p>
          <dl className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
            <CoverageTooltipRow
              label={t("stores.map.coverage.tooltip.signal")}
              value={rssi === EMPTY_VALUE ? rssi : `${rssi} dBm`}
            />
            <CoverageTooltipRow label={t("stores.map.coverage.tooltip.samples")} value={String(point.sampleCount)} />
            <CoverageTooltipRow label={t("stores.map.coverage.tooltip.accessPoints")} value={String(point.apCount)} />
            <CoverageTooltipRow
              label={t("stores.map.coverage.tooltip.uptime")}
              value={uptime === EMPTY_VALUE ? uptime : `${uptime}%`}
            />
            {point.apMac && (
              <CoverageTooltipRow label={t("stores.map.coverage.tooltip.bssid")} value={point.apMac} mono />
            )}
          </dl>
        </div>
      </TooltipContent>
    </Tooltip>
  );
});

/**
 * Memoised on purpose, and it is the pan that pays for it.
 *
 * Panning sets state on every pointermove, which re-renders the tab and, with it,
 * this whole subtree — one cloud circle plus one Radix root per camera, sixty times
 * a second, for a picture that has not changed. The transform lives on a div above,
 * so the overlay's own props are identical throughout a drag: skipping the render
 * makes dragging cost the same whether the store has three cameras or three hundred.
 */
export const StoreMapCoverageOverlay = memo(function StoreMapCoverageOverlay({
  points,
  naturalWidth,
  naturalHeight,
  storeMask,
  className,
}: StoreMapCoverageOverlayProps) {
  const { t } = useTranslation("back-office");
  const maskReady = useMaskReady(storeMask);
  // Ids are global to the document, and two maps can be mounted at once (store
  // compare). Scoping them per instance stops one overlay clipping against another's mask.
  const instanceId = useId();

  // Nothing at all until the clip is usable: a cloud spilling across the car park
  // for one frame is worse than the overlay appearing a beat later.
  if (!naturalWidth || !naturalHeight || !maskReady) return null;

  const maskId = `store-interior-${instanceId}`;

  return (
    <svg
      // The viewBox IS the image's own pixel grid, and `xMidYMid meet` letterboxes
      // exactly the way `object-contain` does on the <img> beneath. That equivalence
      // is what lets a point render at its raw (x, y) with no conversion anywhere.
      viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
      preserveAspectRatio="xMidYMid meet"
      className={cn("pointer-events-none absolute inset-0 h-full w-full", className)}
      role="img"
      aria-label={t("stores.map.coverage.overlayLabel")}
    >
      <defs>
        {storeMask && (
          <mask id={maskId}>
            {/* Luminance mask: the PNG is white over the building and black outside, so
                the cloud survives only indoors. `none` because the mask spans the same
                pixel grid as the map and must not be letterboxed independently of it. */}
            <image
              href={`data:image/png;base64,${storeMask}`}
              x={0}
              y={0}
              width={naturalWidth}
              height={naturalHeight}
              preserveAspectRatio="none"
            />
          </mask>
        )}
        {Object.entries(COVERAGE_COLORS).map(([quality, color]) => (
          <radialGradient key={quality} id={`coverage-${quality}-${instanceId}`}>
            <stop offset="0%" stopColor={color} stopOpacity={CLOUD_CORE_OPACITY} />
            <stop offset="55%" stopColor={color} stopOpacity={CLOUD_MID_OPACITY} />
            {/* Fading to fully transparent is what makes overlapping samples blend
                into a field instead of stacking as visible discs. */}
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </radialGradient>
        ))}
      </defs>

      {/* Opacity on the group, not on each circle: the samples blend with one another
          first and the result is composited once, so the map reads through the field
          evenly instead of getting darker wherever discs happen to overlap. */}
      <g opacity={CLOUD_LAYER_OPACITY} mask={storeMask ? `url(#${maskId})` : undefined}>
        {points.map((point) => (
          <circle
            key={`cloud-${point.cameraId}`}
            cx={point.x}
            cy={point.y}
            r={SAMPLE_RADIUS}
            fill={`url(#coverage-${point.quality}-${instanceId})`}
          />
        ))}
      </g>

      {/* Hoverable content is off: these triggers are 26px dots the pointer sweeps
          across while reading the map, and a tooltip that survives the pointer
          leaving its dot would sit over the neighbour you moved on to. */}
      <TooltipProvider delayDuration={150} disableHoverableContent>
        <g className="pointer-events-auto">
          {points.map((point) => (
            <CoveragePointDot key={`dot-${point.cameraId}`} point={point} />
          ))}
        </g>
      </TooltipProvider>
    </svg>
  );
});
